import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';

interface CoreInputProps {
  autocomplete?: string;
  behindValue?: string;
  type?: string;
  labelFor?: string;
  dataType?: string;
  inputmode?: any;
  className?: any;
  placeholder?: string;
  value: string;
  digits?: string;
  maxLength?: number;
  allCaps?: boolean;
  disabled?: boolean;
  onTextChange?: (string: string) => void;
  onFocus?(): void;
  onBlur?(): void;
  onKeyPressEnter?(): void;
  onKeyDown?(): void;
  inputButton?(): void;
  executeAfterTextChange?(): void;
}

const CoreInput = forwardRef<HTMLInputElement, CoreInputProps>(
  (
    {
      autocomplete,
      behindValue,
      dataType,
      labelFor,
      className,
      type = 'text',
      inputmode = '',
      placeholder = '',
      value = '',
      digits = '',
      maxLength,
      allCaps = false,
      onTextChange,
      onFocus,
      onBlur,
      onKeyPressEnter,
      onKeyDown,
      disabled,
      inputButton,
      executeAfterTextChange,
    },
    ref,
  ) => {
    const [state, setState] = useState({ text: '' });
    const [isComposition, setIsComposition] = useState(true);
    const { text } = state;
    const debounceExecute = useCallback(
      debounce(() => {
        if (executeAfterTextChange) executeAfterTextChange();
      }, 500),
      [executeAfterTextChange],
    );

    useEffect(() => {
      if (text !== value) {
        setState({ text: value });
      }
    }, [text, value]);

    useEffect(() => {
      if (isComposition && executeAfterTextChange) {
        debounceExecute();
      }
    }, [isComposition, text]);

    const onKeyChange = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && onKeyPressEnter && isComposition) {
        onKeyPressEnter();
      }
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;

      if (behindValue && value.match(`^${behindValue}.*`)) {
        value = value.replace(behindValue, '');
      }

      if (matchDigits(value) || isDeleteMode(value)) {
        setState({ text: displayText(value) });
        if (onTextChange) {
          onTextChange(displayText(value));
        }
      } else {
        setState({ text: displayText(text) });
      }
    };

    const isDeleteMode = (value: string) => {
      return value.length < text.length;
    };

    const matchDigits = (value: string) => {
      const latestChar = value.charAt(value.length - 1);
      if (digits !== '' && value !== '') {
        return latestChar.match(digits) !== null;
      } else {
        return true;
      }
    };

    const displayText = (text: string) => {
      if (allCaps) {
        return text.toUpperCase();
      } else {
        return text;
      }
    };

    return (
      <>
        <input
          autoComplete={autocomplete}
          name={labelFor}
          disabled={disabled}
          ref={ref}
          datatype={dataType}
          className={className}
          type={type}
          inputMode={inputmode}
          placeholder={placeholder}
          value={text}
          maxLength={maxLength}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyPress={onKeyChange}
          onKeyDown={onKeyDown}
          onCompositionStart={() => setIsComposition(false)}
          onCompositionEnd={() => setIsComposition(true)}
        />
        {inputButton}
      </>
    );
  },
);

export default CoreInput;
